import { jwtDecode } from "jwt-decode";
import { RelativeUrl } from "./environment";
import { Role } from "../types/auth";
import LocalStorageService from "./storage";
import SecureStorageService from "./secure-storage";
import { BrandStatus } from "../types/brand";
import { parseResponse, setUnauthorizedHandler } from "./response";
import Result from "../types/result";
import { setRedirectUrl, getFullCurrentUrl } from "./redirect-url";
import DataDogManager from "./datadog";
const AuthTokenKey = "adora-auth-token";
const RefreshTokenKey = "adora-refresh-token";
const UserKey = "adora-user";

type AuthListener = () => void;
const authListeners = new Set<AuthListener>();

const notifyAuthListeners = () => {
  authListeners.forEach((listener) => listener());
};

export const subscribeToAuth = (listener: AuthListener) => {
  authListeners.add(listener);
  return () => {
    authListeners.delete(listener);
  };
};

const LoginApi = RelativeUrl + "/login";
const RefreshTokenApi = RelativeUrl + "/refresh-token";
const SwitchBrandApi = RelativeUrl + "/switch-brand";

export const LOGOUT_KEY = "adora-logout";
export const AUTH_RESYNC_EVENT = "adora:auth-resynced";
export const LOGOUT_STARTED_EVENT = "adora:logout-started";
export const LOGOUT_IN_PROGRESS_KEY = "adora-logout-in-progress";

/** SessionStorage key set by web app before full-page navigate on brand switch. Cleared after app-sidebar runs post-switch redirect check (redirect to /home if path not in nav). */
export const BRAND_SWITCH_PENDING_KEY = "adora-brand-switch-pending";

export const TAB_ID = crypto.randomUUID();

const setAuthToken = (token: string) => {
  SecureStorageService.setItem(AuthTokenKey, token);
};

const setRefreshToken = (token: string) => {
  SecureStorageService.setItem(RefreshTokenKey, token);
};

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  tokenType: "bearer";
  brand_status: BrandStatus;
}

export interface RefreshAuthPayload {
  brandId?: number;
  redirectPath?: string;
  trigger?: "manual" | "deep-link";
  sentAt?: number;
}

export interface ExternalInfo {
  role?: string;
  is_agency?: boolean;
  seniority?: string;
}

export interface AdoraUser {
  id: string;
  name: string;
  brandId: number;
  role: Role;
  expiration: number;
  version: number;
  brandStatus: BrandStatus;
  external_info?: ExternalInfo;
}

export interface AdoraInternalUser {
  id: string;
  role: Role;
  expiration: number;
  version: number;
}

interface JWTPayload {
  sub: string;
  brand_id: number;
  exp: number;
  user_role: Role;
  user_name: string;
  external_info?: ExternalInfo;
}

const LatestVersion = 5;
let cachedUserStorageValue: string | null = null;
let cachedAdoraUser: AdoraUser | null = null;
let logoutInProgress = false;
let logoutResetTimer: number | null = null;

const setLogoutInProgress = (nextValue: boolean) => {
  logoutInProgress = nextValue;

  if (typeof window === "undefined") {
    return;
  }

  if (logoutResetTimer !== null) {
    window.clearTimeout(logoutResetTimer);
    logoutResetTimer = null;
  }

  if (!nextValue) {
    window.sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
    return;
  }

  window.sessionStorage.setItem(LOGOUT_IN_PROGRESS_KEY, "true");
  window.dispatchEvent(new Event(LOGOUT_STARTED_EVENT));
  logoutResetTimer = window.setTimeout(() => {
    logoutInProgress = false;
    logoutResetTimer = null;
    window.sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
  }, 5000);
};

export const isLogoutInProgress = () => logoutInProgress;

const getBrandSwitchContext = (targetBrandId: number, payload?: RefreshAuthPayload) => ({
  source_brand_id: getAdoraUser()?.brandId ?? null,
  target_brand_id: targetBrandId,
  trigger: payload?.trigger ?? "manual",
  has_redirect_path: Boolean(payload?.redirectPath)
});

const setAdoraUser = (
  user: string,
  name: string,
  brandId: number,
  role: Role,
  expiration: number,
  brandStatus: BrandStatus,
  external_info?: ExternalInfo
): AdoraUser => {
  setLogoutInProgress(false);
  const adoraUser: AdoraUser = {
    id: user,
    name,
    brandId,
    role,
    expiration,
    version: LatestVersion,
    brandStatus,
    external_info
  };
  const serializedUser = JSON.stringify(adoraUser);
  cachedUserStorageValue = serializedUser;
  cachedAdoraUser = adoraUser;
  LocalStorageService.setItem(UserKey, serializedUser);
  notifyAuthListeners();
  return adoraUser;
};

export const getAdoraUser = () => {
  const user = LocalStorageService.getItem(UserKey);
  if (user === cachedUserStorageValue) {
    return cachedAdoraUser;
  }

  if (!user) {
    cachedUserStorageValue = null;
    cachedAdoraUser = null;
    return null;
  }

  const adoraUser = JSON.parse(user) as AdoraUser;
  if (adoraUser.version === LatestVersion) {
    cachedUserStorageValue = user;
    cachedAdoraUser = adoraUser;
    return adoraUser;
  }

  cachedUserStorageValue = null;
  cachedAdoraUser = null;
  Auth.logout();
  return null;
};

export const syncAuthFromStorage = () => {
  cachedUserStorageValue = null;
  cachedAdoraUser = null;

  const user = getAdoraUser();
  notifyAuthListeners();

  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_RESYNC_EVENT));
  }

  return user;
};

/** Returns true when the user has internal role (same meaning as useIsInternalUser in the web app). */
export const isInternalUser = (user: AdoraUser | null): boolean => user?.role === "internal";

export const isActiveBrand = (user: AdoraUser) => {
  const brandStatus = user.brandStatus;

  // TODO: Remove this once BE changes are complete. Brand Status should always have a value
  if (brandStatus === undefined) {
    return true;
  }

  return brandStatus === "active" || brandStatus === "trial";
};

const decodeToken = (response: LoginResponse) => {
  const refreshToken = response.refresh_token;
  if (refreshToken) {
    setRefreshToken(refreshToken);
  }

  const token = response.access_token;

  if (!token) {
    throw "No token found in response";
  }

  setAuthToken(token);
  const decodedToken = jwtDecode<JWTPayload>(token);
  return setAdoraUser(
    decodedToken.sub,
    decodedToken.user_name,
    decodedToken.brand_id,
    decodedToken.user_role,
    decodedToken.exp,
    response.brand_status,
    decodedToken.external_info
  );
};

let refreshTokenPromise: Promise<AdoraUser | null> | null = null;

export const redirectToLogin = (includeRedirectUrl: boolean = true, redirectUrl?: string | null) => {
  if (window.location.pathname === "/login") return;
  const fullUrl = window.location.pathname + window.location.search + window.location.hash;
  const url = "/login" + (includeRedirectUrl ? "?redirect_url=" + encodeURIComponent(redirectUrl || fullUrl) : "");
  window.location.href = url;
};

const refreshAccessToken = async () => {
  const refreshToken = SecureStorageService.getItem(RefreshTokenKey);
  const currentUser = getAdoraUser();

  const response = await fetch(RefreshTokenApi, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({
      refresh_token: refreshToken,
      brand_id: currentUser?.brandId // Include current brand_id to preserve context
    })
  });

  const result = await parseResponse<LoginResponse>(response);

  return result.match(
    () => {
      // Force a logout if the refresh token is expired, preserving redirect URL
      Auth.logout("preserve-redirect");
      return null;
    },
    (value) => decodeToken(value)
  );
};

const Auth = {
  login: async (formData: FormData): Promise<Result<AdoraUser>> => {
    const requestOptions = {
      method: "POST",
      body: formData
    };

    const response = await fetch(LoginApi, requestOptions);
    const result = await parseResponse<LoginResponse>(response);

    return result.map(decodeToken);
  },
  logout: (redirect?: boolean | "preserve-redirect") => {
    setLogoutInProgress(true);

    // Capture URL BEFORE clearing (in case we need it)
    const currentUrl = redirect === "preserve-redirect" ? getFullCurrentUrl() : null;

    SecureStorageService.clear();
    cachedUserStorageValue = null;
    cachedAdoraUser = null;
    LocalStorageService.setItem(LOGOUT_KEY, Math.random().toString());

    // Save redirect URL AFTER clearing storage (so it doesn't get wiped)
    if (currentUrl) {
      setRedirectUrl(currentUrl);
    }

    if (redirect) {
      redirectToLogin(false);
    }
  },
  getBearerToken: async (): Promise<string> => {
    const user = getAdoraUser();
    const authToken = SecureStorageService.getItem(AuthTokenKey);

    if (!user || !authToken) {
      Auth.logout("preserve-redirect");
      return "";
    }

    const bufferTime = 60; // 1 minutes in seconds
    const isExpiringSoon = user.expiration < Date.now() / 1000 + bufferTime;

    if (!isExpiringSoon) {
      return `Bearer ${authToken}`;
    }

    try {
      // Only allow one refresh token request at a time
      if (!refreshTokenPromise) {
        refreshTokenPromise = refreshAccessToken();
      }

      const refreshedUser = await refreshTokenPromise;

      if (!refreshedUser) {
        Auth.logout("preserve-redirect");
        return "";
      }

      const newToken = SecureStorageService.getItem(AuthTokenKey);
      return newToken ? `Bearer ${newToken}` : "";
    } catch {
      Auth.logout("preserve-redirect");
      return "";
    } finally {
      refreshTokenPromise = null;
    }
  },
  switchBrand: async (brandId: number, payload?: RefreshAuthPayload): Promise<Result<AdoraUser>> => {
    const brandSwitchContext = getBrandSwitchContext(brandId, payload);
    DataDogManager.reportCustomAction("brand_switch_started", brandSwitchContext);

    try {
      const response = await fetch(SwitchBrandApi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: await Auth.getBearerToken()
        },
        body: JSON.stringify({
          brand_id: brandId
        })
      });

      const result = await parseResponse<LoginResponse>(response);
      if (result.isError()) {
        DataDogManager.reportCustomAction("brand_switch_failed", {
          ...brandSwitchContext,
          failure_stage: "switch_brand_api"
        });
        return result.map(decodeToken);
      }

      SecureStorageService.clearAuthKeys();
      const updatedResult = result.map(decodeToken);
      Auth.refreshOtherTabs({ brandId, ...payload });
      DataDogManager.reportCustomAction("brand_switch_completed", brandSwitchContext);
      return updatedResult;
    } catch (error) {
      DataDogManager.reportCustomAction("brand_switch_failed", {
        ...brandSwitchContext,
        failure_stage: error instanceof Error ? error.name : "unknown"
      });
      throw error;
    }
  },
  refreshOtherTabs: (payload?: RefreshAuthPayload) => {
    const nextPayload = {
      ...payload,
      sentAt: payload?.sentAt ?? Date.now()
    };

    DataDogManager.reportCustomAction("auth_refresh_broadcast_sent", {
      target_brand_id: nextPayload.brandId ?? null,
      trigger: nextPayload.trigger ?? "manual",
      has_redirect_path: Boolean(nextPayload.redirectPath)
    });

    const channel = new BroadcastChannel("auth-refresh");
    channel.postMessage({ type: "REFRESH_AUTH", ...nextPayload, senderId: TAB_ID });
    channel.close();
  }
};

// Auto-logout when the backend signals the session is dead.
// Backend tags such 401s with `WWW-Authenticate: Bearer`; per-request authz
// failures (wrong brand, login fail, OTC, etc.) intentionally do not.
setUnauthorizedHandler((response) => {
  if (isLogoutInProgress()) return;

  const wwwAuth = response.headers.get("WWW-Authenticate") ?? "";
  if (!wwwAuth.toLowerCase().startsWith("bearer")) return;

  // Endpoints that own their own 401 handling.
  if (response.url.endsWith(RefreshTokenApi) || response.url.endsWith(LoginApi)) return;

  // Avoid redirect loops if a 401 fires while already on /login.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/login")) return;

  Auth.logout("preserve-redirect");
});

export default Auth;
